import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Trash2, Phone, Mail, Building2, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exports";

const BU_VARIANTS = { FigureWeb: "info", FigureCX: "aqua", Telecom: "purple" };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [buFilter, setBuFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", business_unit: "FigureWeb", source: "website" });

  const load = () => api.get("/contacts").then(r => setContacts(r.data));
  useEffect(() => { load(); }, []);

  const filtered = contacts.filter(c => {
    if (buFilter !== "all" && c.business_unit !== buFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) ||
           (c.company || "").toLowerCase().includes(s) ||
           (c.email || "").toLowerCase().includes(s);
  });

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/contacts", form);
    toast.success("Contact added");
    setOpen(false);
    setForm({ name: "", company: "", email: "", phone: "", business_unit: "FigureWeb", source: "website" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contacts"
        subtitle={`${contacts.length} contacts across all business units`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("contacts")} data-testid="export-contacts-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> New Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Full name</Label>
                  <Input data-testid="contact-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                <div><Label>Company</Label>
                  <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                  <div><Label>Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Source</Label>
                    <Select value={form.source} onValueChange={v => setForm({...form, source: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="cold_call">Cold call</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-contact-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <Input placeholder="Search contacts, companies, emails..." value={q} onChange={e => setQ(e.target.value)}
               className="sm:max-w-xs" data-testid="contacts-search" />
        <Select value={buFilter} onValueChange={setBuFilter}>
          <SelectTrigger className="sm:w-52"><SelectValue placeholder="Business unit" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All business units</SelectItem>
            <SelectItem value="FigureWeb">FigureWeb</SelectItem>
            <SelectItem value="FigureCX">FigureCX</SelectItem>
            <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Business Unit</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="row" data-testid={`contact-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-slate-400" />{c.company || "—"}</span></td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="flex flex-col text-xs">
                    {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{c.email}</span>}
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{c.phone}</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><Chip variant={BU_VARIANTS[c.business_unit] || "default"}>{c.business_unit}</Chip></td>
                <td className="px-4 py-3 text-slate-500 capitalize">{c.source?.replace("_", " ")}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-contact-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No contacts match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
